import React, { useCallback, useEffect, useState } from "react";
import { Button, Text, useToast } from "@bookingcom/bui-react";
import useModalDialog from "../../hooks/useModalDialog";
import styles from "./ScreenSizeChangeModal.module.css";
import useGlobalContext from "../../hooks/useGlobalContext";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useLocation } from "react-router-dom";
import useClientCachedURL from "../../hooks/useClientCachedURL";
import useClientMetrics from "../../hooks/useClientMetrics";
import { customGoal, stage, trackWithDefaultStage } from "@flights/et-universal";
import useRouteName from "../../hooks/useRouteName";
import useIsPostBookingRoute from "../../hooks/useIsPostBookingRoute";

const BREAKPOINT = 1000;
const DEBOUNCE_DELAY = 250; // 250ms delay
const TOAST_DURATION = 8000;
const ScreenSizeChangeModal = () => {
  const Modal = useModalDialog();
  const { userAgent } = useGlobalContext();
  const { trackClientMetric } = useClientMetrics();
  const [showModal, setShowModal] = useState(false);
  const [hasClosedModal, setHasClosedModal] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= BREAKPOINT);
  const [isMounted, setIsMounted] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const i18n = useI18n();
  const { show: showToast } = useToast();
  const location = useLocation();
  const routeName = useRouteName();
  const { getLastSuccessfulSearchUrl } = useClientCachedURL();
  const STORAGE_KEY = "_flights_screen_size_modal_pref";
  const isPostBooking = useIsPostBookingRoute();

  const isResponsiveFixesPb = Boolean(
    isPostBooking && trackWithDefaultStage("flights_web_cat_responsive_fixes_pb_www", 1)
  );

  // Add window resize listener with debounce
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newIsSmallScreen = window.innerWidth <= BREAKPOINT;
        setScreenSize(window.innerWidth);
        setIsSmallScreen(newIsSmallScreen);
        setHasClosedModal(false);
      }, DEBOUNCE_DELAY);
    };
    // Add resize listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [isSmallScreen, userAgent.isMobile]);

  useEffect(() => {
    stage("flights_a11y_screen_resize_issue_web", 1);
    setIsMounted(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueHere = () => {
    sessionStorage.setItem(STORAGE_KEY, "continue");
    setShowModal(false);
    showToast(
      {
        text: i18n.trans(t("a11y_modal_zoom_toast_continue_body")),
        action: {
          text: i18n.trans(t("a11y_modal_zoom_toast_cta_go_back")),
          onClick: () => removeCookie()
        }
      },
      TOAST_DURATION
    );
  };

  const removeCookie = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    trackClientMetric("xm_www_screensizechangemodal_clicked_remove_use_mobile_view");
  }, [trackClientMetric]);

  const redirectToIndex = useCallback(() => {
    // For index page, redirect to root with a11y_mobile_view parameter
    const params = new URLSearchParams();
    params.set("a11y_mobile_view", "1");
    trackClientMetric("xm_www_screensizechangemodal_redirect_to_index");
    window.location.href = `${location.pathname}?${params}`;
  }, [location.pathname, trackClientMetric]);

  const redirectToSearch = useCallback(() => {
    const searchUrl = getLastSuccessfulSearchUrl();
    if (searchUrl) {
      const [path, queryString] = searchUrl.split("?");
      if (queryString) {
        const searchUrlParams = new URLSearchParams(queryString);
        searchUrlParams.set("a11y_mobile_view", "1");
        trackClientMetric("xm_www_screensizechangemodal_redirect_to_search");
        window.location.href = `${path}?${searchUrlParams.toString()}`;
      }
    } else {
      redirectToIndex();
    }
  }, [getLastSuccessfulSearchUrl, trackClientMetric, redirectToIndex]);

  const redirect = useCallback(
    (pref: string) => {
      setShowModal(false);
      if (pref) {
        showToast(
          {
            text: i18n.trans(t("a11y_modal_zoom_toast_body")),
            action: {
              text: i18n.trans(t("a11y_modal_zoom_toast_cta_cancel")),
              onClick: () => removeCookie()
            }
          },
          TOAST_DURATION
        );
      } else {
        showToast(
          {
            text: i18n.trans(t("a11y_modal_zoom_toast_continue_body")),
            action: {
              text: i18n.trans(t("a11y_modal_zoom_toast_cta_go_back")),
              onClick: () => removeCookie()
            }
          },
          TOAST_DURATION
        );
      }

      setTimeout(() => {
        const updatedPref = sessionStorage.getItem(STORAGE_KEY);
        if (updatedPref) {
          if (routeName === "home") {
            customGoal("flights_a11y_screen_resize_issue_web", 3);
            redirectToIndex();
          } else {
            customGoal("flights_a11y_screen_resize_issue_web", 4);
            redirectToSearch();
          }
        }
      }, TOAST_DURATION + 500); // adding some delay post toast closure
    },
    [redirectToIndex, redirectToSearch, i18n, showToast, removeCookie, routeName]
  );

  const handleViewBest = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "viewbest");
    redirect("viewbest");
    trackClientMetric("xm_www_screensizechangemodal_clicked_use_mobile_view");
  }, [redirect, trackClientMetric]);

  // Modified effect to use isSmallScreen instead of isMdOrDown
  useEffect(() => {
    if (!isSmallScreen) {
      setShowModal(false);
      setHasClosedModal(false);
    }
    if (isMounted && isSmallScreen && !userAgent.isMobile && !hasClosedModal) {
      // affected users which are not already mobile and switched to small screen
      if (!trackWithDefaultStage("flights_a11y_screen_resize_issue_web", 2)) {
        return;
      }
      const pref = sessionStorage.getItem(STORAGE_KEY);
      if (!pref) {
        customGoal("flights_a11y_screen_resize_issue_web", 1);
        setShowModal(true);
        trackClientMetric("xm_www_screensizechangemodal_shown");
      } else {
        const pref = sessionStorage.getItem(STORAGE_KEY);
        if (pref === "viewbest") {
          customGoal("flights_a11y_screen_resize_issue_web", 2);
          redirect(pref);
        }
      }
    }
  }, [isSmallScreen, isMounted, userAgent.isMobile, redirect, trackClientMetric, hasClosedModal, screenSize]);

  if (isResponsiveFixesPb) {
    return null;
  }

  return (
    showModal && (
      <Modal
        title={i18n.trans(t("a11y_modal_zoom_header"))}
        active={true}
        onClose={() => {
          setShowModal(false);
          setHasClosedModal(true);
        }}
        closeAriaLabel={""}
      >
        <div className={styles.modalContent}>
          <Text className={styles.modalBody} variant="body_2">
            {i18n.trans(t("a11y_modal_zoom_body"))}
          </Text>

          <div className={styles.buttonContainer}>
            <Button
              variant="secondary"
              size="large"
              className={styles.continueButton}
              text={i18n.trans(t("a11y_modal_zoom_cta_continue"))}
              onClick={handleContinueHere}
            />
            <Button
              variant="primary"
              size="large"
              className={styles.viewBestButton}
              text={i18n.trans(t("a11y_modal_zoom_cta_switch"))}
              onClick={handleViewBest}
            />
          </div>
        </div>
      </Modal>
    )
  );
};

export default ScreenSizeChangeModal;
